import { raiseToast } from '@fiverr-private/toast';
import { triggerSignUp } from '../../../../triggers/api';
import { AfterSigningSuccess } from '../../../../shared/constants/afterSigningSuccess';
import {
    AUTHENTICATION_SIGN_IN_FLOW_TYPES,
    AuthenticationResponse,
} from '../../../../shared/constants/authenticationFlow';

import { translate } from '../../../../utils/translate';
import { getAuthenticationFlowType } from '../../../../utils/getAuthenticationFlowType';
import { getSigningFlowType } from '../../../../utils/getSigningFlowType';
import { globalMixpanelReporter } from '../../../../events/mixpanel/mixpanel.reporter';
import { globalBigQueryReporter } from '../../../../events/bigQuery/bigQuery.reporter';
import { globalGrafanaReporter } from '../../../../events/grafana/grafana.reporter';
import { reportIdentificationCompletion } from '../../../../utils/reportIdentificationCompletion';

interface UseGoogleOneTapHandlersParams {
    afterSigningSuccess?: AfterSigningSuccess;
}

export const useGoogleOneTapHandlers = ({ afterSigningSuccess }: UseGoogleOneTapHandlersParams) => {
    const onOneTapSuccess = (authenticationResponse: AuthenticationResponse) => {
        const { result_hash: result, url: redirectUrl } = authenticationResponse;
        const { flow_type: flowType } = result;
        const authenticationFlowType = getAuthenticationFlowType(flowType);

        if (!authenticationFlowType) {
            return;
        }

        if (AUTHENTICATION_SIGN_IN_FLOW_TYPES.includes(authenticationFlowType)) {
            globalGrafanaReporter.count('identification.%{platform}.authentication_completed');
            globalBigQueryReporter.send('authentication_completed');

            globalMixpanelReporter.send('Identification - Authentication - Succeeded');
            globalMixpanelReporter.send('Logged in successfully');

            reportIdentificationCompletion();

            if (afterSigningSuccess) {
                afterSigningSuccess({
                    signingFlowType: getSigningFlowType({
                        mode: 'sign_in',
                        isSocialFlow: true,
                    }),
                    redirectUrl,
                });

                return;
            }

            if (redirectUrl) {
                window.location.assign(redirectUrl);
                return;
            }

            window.location.reload();
            return;
        }

        const { user_data: userData = {} } = result;

        if (!userData?.suggested_email) {
            triggerSignUp({
                modalOptions: {
                    stepId: 'social_fallback',
                    isSocialFlow: true,
                },
                biEnrichmentParams: {
                    googleOneTap: true,
                },
            });

            return;
        }

        const { suggested_email: email, suggested_username: username } = userData;

        triggerSignUp({
            modalOptions: {
                stepId: 'profile_setup',
                skipActivation: true,
                isSocialFlow: true,
                initialValues: {
                    email,
                    username,
                },
            },
            biEnrichmentParams: {
                googleOneTap: true,
            },
        });
    };

    const onOneTapError = () => {
        globalGrafanaReporter.count(
            'social_signing.%{platform}.google_one_tap.error.error_while_authenticating_credential'
        );

        raiseToast({
            type: 'error',
            location: 'top',
            message: translate('social.errors.authentication', {
                params: {
                    provider: 'Google',
                },
            }),
        });
    };

    return {
        onOneTapSuccess,
        onOneTapError,
    };
};
